// api/common/ChatHubService.ts
import type { HubConnection } from '@microsoft/signalr';
import { SignalRServiceBase, type SignalRConnectionOptions } from './SignalRServiceBase';

/**
 * Loosely-typed shape for the `OnContactReceived` payload — the backend DTO isn't
 * finalized yet (a parallel change is adding the event). `Record<string, unknown>`
 * keeps this forward-compatible; consumers narrow the best-guess fields below as needed.
 */
export type ContactReceivedPayload = Record<string, unknown> & {
  id?: string;
  name?: string;
  email?: string;
  subject?: string;
  submittedAt?: string;
};

/**
 * Client for the backend `CrmChatHub`, routed through the gateway at `/hubs/chat`.
 * First concrete subclass of SignalRServiceBase — reconnection, auth-token handling,
 * and event on/off/trigger plumbing all come from the base class; this just wires up
 * the one hub-pushed event this app cares about today: `OnContactReceived`.
 */
export class ChatHubService extends SignalRServiceBase {
  constructor(options: Omit<SignalRConnectionOptions, 'hubName'> = {}) {
    super({ ...options, hubName: '/hubs/chat' });
  }

  protected async setupCustomEventHandlers(connection: HubConnection): Promise<void> {
    connection.on('OnContactReceived', (payload: ContactReceivedPayload) => {
      this.triggerEvent('OnContactReceived', payload);
    });
  }
}
